"use client"

import { useState, useRef, useEffect } from 'react'
import { lazy, Suspense } from 'react'
import * as stylex from '@stylexjs/stylex'
import { useMediaContext } from '@/context/MediaContext'
import { VideoGalleryItem } from '@/models/gallery'
import { videoPageStyles } from '@/styles/VideoPage.stylex'

const VideoPopupRaw = lazy(() => import('@/components/popup/VideoPopup'));
const VideoPopup = (props: any) => (
    <Suspense fallback={null}>
        <VideoPopupRaw {...props} />
    </Suspense>
);

interface TextImageToVideoGalleryProps {
    title: string
    description: string
    modelKey: string
    modelLabel: string
    modelNote: string
    allImages: string[]
    items: VideoGalleryItem[]
}

export function TextImageToVideoGallery({ title, description, modelKey, modelLabel, modelNote, items, allImages }: TextImageToVideoGalleryProps) {
    const [showPopup, setShowPopup] = useState(false)
    const [selectedItem, setSelectedItem] = useState<VideoGalleryItem>({ id: '', videoSrc: '', className: '', prompt: '', aspect_ratio: '', referenceImages: [] })
    const { registerMediaElement, unregisterMediaElement, onMediaPlay } = useMediaContext()
    const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map())

    // Register all video elements with MediaContext
    useEffect(() => {
        videoRefs.current.forEach((video, id) => {
            registerMediaElement(id, video)
        })
        return () => {
            videoRefs.current.forEach((_, id) => {
                unregisterMediaElement(id)
            })
        }
    }, [registerMediaElement, unregisterMediaElement, items])

    const handleVideoHover = (videoElement: HTMLVideoElement, videoId: string, isEntering: boolean) => {
        if (isEntering) {
            onMediaPlay(videoId)
            videoElement.muted = false
            videoElement.play().catch((error) => {
                if (error.name === 'AbortError') return
                videoElement.muted = true
                videoElement.play().catch(() => { })
            })
        } else {
            videoElement.pause()
            videoElement.muted = true
        }
    }

    const openPopup = (item: VideoGalleryItem) => {
        setSelectedItem(item)
        setShowPopup(true)
    }

    return (
        <>
            <div {...stylex.props(videoPageStyles.gallerySection)}>
                <h3>{title}</h3>
                <p dangerouslySetInnerHTML={{ __html: description }} />
                <div {...stylex.props(videoPageStyles.textImageVideoList)}>
                    {items.map((item, index) => (
                        <GalleryItem
                            key={item.id || index}
                            item={item}
                            index={index}
                            videoRefs={videoRefs}
                            onHover={handleVideoHover}
                            onClick={() => openPopup(item)}
                        />
                    ))}
                </div>
            </div>

            <VideoPopup
                isOpen={showPopup}
                onClose={() => setShowPopup(false)}
                id={selectedItem.id}
                videoSrc={selectedItem.videoSrc}
                thumbnailSrc={selectedItem.thumbnailSrc}
                allImages={allImages}
                selectedReferenceImages={selectedItem.referenceImages || []}
                selectedReferenceVideos={selectedItem.referenceVideos || []}
                modelKey={modelKey}
                modelLabel={modelLabel}
                prompt={selectedItem.prompt}
                aspect_ratio={selectedItem.aspect_ratio}
                popupTitle="Video Details"
                modelNote={modelNote}
            />
        </>
    )
}

/**
 * Sub-component to manage per-item hover states for StyleX animations
 */
function GalleryItem({ item, index, videoRefs, onHover, onClick }: any) {
    const [isHovered, setIsHovered] = useState(false);
    const videoId = `text-image-to-video-${item.id || index}`;
    const referenceImages = item.referenceImages || [];

    return (
        <div
            {...stylex.props(videoPageStyles.textImageVideoItem)}
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div {...stylex.props(videoPageStyles.imageVideoPair)}>
                {/* Reference Image Section */}
                {referenceImages.length > 0 && (
                    <div {...stylex.props(videoPageStyles.referenceImage)}>
                        <img
                            src={referenceImages[0]}
                            loading="lazy"
                            decoding="async"
                            alt="Reference"
                            {...stylex.props(videoPageStyles.comparisonImage)}
                        />
                    </div>
                )}

                {/* Arrow Section with hover effect */}
                <div {...stylex.props(videoPageStyles.arrowSection)}>
                    <div {...stylex.props(
                        videoPageStyles.arrow,
                        isHovered && videoPageStyles.arrowHover
                    )}>→</div>
                </div>

                {/* Generated Video Section */}
                <div {...stylex.props(videoPageStyles.generatedVideo)}>
                    <div {...stylex.props(videoPageStyles.videoContainer)}>
                        <video
                            ref={(el) => { if (el) videoRefs.current.set(videoId, el) }}
                            src={item.videoSrc}
                            poster={item.thumbnailSrc}
                            {...stylex.props(videoPageStyles.comparisonVideo)}
                            loop
                            muted
                            preload="none"
                            playsInline
                            onMouseEnter={(e) => onHover(e.target as HTMLVideoElement, videoId, true)}
                            onMouseLeave={(e) => onHover(e.target as HTMLVideoElement, videoId, false)}
                        />
                    </div>
                </div>
            </div>

            {/* Centered Click Indicator with hover effect */}
            <div {...stylex.props(
                videoPageStyles.clickIndicatorCenter,
                isHovered && videoPageStyles.indicatorActive
            )}>
                Click me
            </div>
        </div>
    );
}